import i18next from 'i18next';
import morphdom from 'morphdom';
import {html, render, noChange} from 'lit-html';
import {Directive, directive} from 'lit-html/directive.js';
import resources from '../tmp/i18n-build/catalogs.js';
import languageModule from './language-store.js';
import routes from './routes.js';
import publicPages from './public-pages.js';
import {richTokens,mapRich} from './rich.mjs';

// Legacy controllers own some initially static slots after boot. Lit must not
// attempt to write through markers that those controllers have legitimately removed.
export const localized=directive(class extends Directive {
 render(value){return value;}
 update(part,[value]){return part.startNode && !part.startNode.parentNode ? noChange : value;}
});

const engine=i18next.createInstance();
engine.init({lng:'es',fallbackLng:'es',supportedLngs:['es','en'],initImmediate:false,
  resources:{es:{translation:resources.es},en:{translation:resources.en}},keySeparator:false,
  interpolation:{escapeValue:false},returnEmptyString:false});
const warned=new Set();
export const T=(key,values={})=>{
 const exists=resources[engine.language][key]||Object.keys(resources[engine.language]).some(candidate=>candidate.startsWith(key+'_'));
 if(!exists&&!warned.has(key)&&['localhost','127.0.0.1'].includes(location.hostname)){
  warned.add(key);console.warn('Missing i18n translation:',engine.language,key);
 }
 return engine.t(key,{...values,defaultValue:resources.es[key]||'Texto no disponible'});
};
export function rich(key,slots){return mapRich(richTokens(T(key)),slots,value=>value);}
const displayKeys=new Map(Object.entries(resources.es).filter(([,text])=>!text.includes('{{')).map(([key,text])=>[text,key]));
const bindings=new WeakMap(),renderTargets=new Set(),listeners=new Set();
let shell=()=>{},status='',userId=null,pendingChoices=0;
let languageStatusTimer;
function notifyLanguageStatus(next){
 clearTimeout(languageStatusTimer);
 status=next;renderControls();
 if(next==='saved')languageStatusTimer=setTimeout(()=>{
  status='';renderControls();
 },3000);
}
const attrNames=new Set(['title','placeholder','alt']);
function write(node,property,value,updating=false){
 if(typeof value==='function')value=value();
 if(property==='innerHTML'&&updating){
  const selectedValues=node instanceof HTMLSelectElement?Array.from(node.selectedOptions,option=>option.value):null;
  const copy=node.cloneNode(false);copy.innerHTML=value;
  morphdom(node,copy,{childrenOnly:true,onBeforeElUpdated(from,to){
   // Do not turn a language change into a form reset or an import cancellation.
   if(from instanceof HTMLInputElement){to.value=from.value;to.checked=from.checked;}
   if(from instanceof HTMLTextAreaElement)to.value=from.value;
   if(from instanceof HTMLSelectElement)for(const option of to.options)option.selected=option.value===from.value;
   if(from instanceof HTMLDetailsElement)to.open=from.open;
   for(const name of ['class','style','hidden','disabled','aria-expanded','aria-selected','aria-pressed','data-chart-focused-planet','data-chart-focused-pattern']){
    if(from.hasAttribute(name))to.setAttribute(name,from.getAttribute(name));else to.removeAttribute(name);
   }
   return true;
  }});
  // childrenOnly does not call onBeforeElUpdated for the root select. Restore
  // its actual selection after updating option labels (birth month, time, etc.).
  if(selectedValues)for(const option of node.options)option.selected=selectedValues.includes(option.value);
 }else if(property==='validity')node.setCustomValidity(value);
 else if(property.startsWith('@'))node.setAttribute(property.slice(1),value);
 else if(attrNames.has(property))node.setAttribute(property,value);
 else node[property]=value;
}
export const I18n={
 validity(node,producer){I18n.bind(node,'validity',producer);},
 clear(node,property){bindings.get(node)?.delete(property);node[property]='';},
 replaceChildren(node,...children){bindings.get(node)?.delete('innerHTML');node.replaceChildren(...children);},
 track(node,property,producer){
  if(!node)return;
  let entries=bindings.get(node);if(!entries){bindings.set(node,entries=new Map());renderTargets.add(new WeakRef(node));}
  entries.set(property,producer);
 },
 bind(node,property,producer){
  if(!node)return;I18n.track(node,property,producer);write(node,property,producer());
 },
 subscribe(fn){listeners.add(fn);return()=>listeners.delete(fn);},
 setShell(fn){shell=fn;},
 get language(){return engine.language;},
 label(value){const key=displayKeys.get(value);return key?T(key):value;},
 content(record,field){return record?.translations?.[engine.language]?.[field] || record?.[field] || '';},
 transit(record,field){const identity=[record.transit_planet,record.natal_planet,record.aspect].join('.').normalize('NFD').replace(/[\u0300-\u036f]/g,'').toLowerCase();return T('transits.'+identity+'.'+field);},
 locale(){return engine.language==='en'?'en-GB':'es-ES';},
 setUser(id){if(userId===(id||null))return;userId=id||null;return store.setUser(userId);},
 choose(language){return choose(language);},
 routePath(){return routes.publicRoute(location.pathname)?.path || location.pathname;},
 pushState(state,title,url){navigate('pushState',state,title,url);},
 replaceState(state,title,url){navigate('replaceState',state,title,url);},
 fetch(input,options={}){
  const url=new URL(typeof input==='string'?input:input.url,location.href);
  if(url.origin!==location.origin||!url.pathname.startsWith('/api/'))return fetch(input,options);
  const headers=new Headers(options.headers || (input instanceof Request?input.headers:undefined));
  headers.set('X-App-Language',engine.language);return fetch(input,{...options,headers});
 },
 refresh: renderLanguage
};
function navigate(method,state,title,url){
 const target=new URL(url || location.href,location.href);
 if(target.origin===location.origin)target.pathname=routes.publicPath(target.pathname,engine.language);
 history[method](state,title,target);syncRoute();
}
function syncRoute(){renderLanguage(routes.publicRoute(location.pathname)?.language || store.language);}
function renderLanguage(language){
 if(!routes.valid(language))language='es';
 const changed=engine.language!==language;
 engine.changeLanguage(language);document.documentElement.lang=language;
 const metadata=publicPages.pageMetadata(location.pathname,location.origin);
 const ensure=(tag,selector,attributes)=>{let node=document.head.querySelector(selector);if(!node){node=document.createElement(tag);document.head.appendChild(node);}for(const[name,value]of Object.entries(attributes))node.setAttribute(name,value);};
 if(metadata){
  document.title=metadata.title;
  ensure('meta','meta[name="description"]',{name:'description',content:metadata.description});
  ensure('meta','meta[name="robots"]',{name:'robots',content:'index,follow'});
  ensure('link','link[rel="canonical"]',{rel:'canonical',href:metadata.canonical});
  for(const code of ['es','en','x-default'])ensure('link',`link[hreflang="${code}"]`,{rel:'alternate',hreflang:code,href:metadata.alternates[code==='x-default'?'es':code]});
 }else{
  document.title=T('common.privatePageTitle');
  ensure('meta','meta[name="robots"]',{name:'robots',content:'noindex,nofollow'});
  document.head.querySelectorAll('link[rel="canonical"],link[hreflang]').forEach(node=>node.remove());
 }
 if(changed){shell();
  for(const reference of [...renderTargets]){const node=reference.deref();if(!node||!node.isConnected){renderTargets.delete(reference);if(node)bindings.delete(node);continue;}for(const[property,fn]of bindings.get(node)||[])write(node,property,fn(),true);}
  for(const fn of listeners)fn(language);
 }
 renderControls();
}
async function choose(language){
 if(!routes.valid(language))return false;
 pendingChoices++;
 try{return await store.choose(language);}finally{pendingChoices--;}
}
let storage;try{storage=window.localStorage;}catch{}
const store=languageModule.createLanguageStore({storage,
 async request(method,language){const response=await fetch('/api/account/language',{method,cache:'no-store',...(language?{headers:{'Content-Type':'application/json'},body:JSON.stringify({language})}:{})});
  const result=await response.json();if(!response.ok)throw new Error('APP_LANGUAGE_UNAVAILABLE');return result;},
 apply(language){
  const route=routes.publicRoute(location.pathname);
  if(route && pendingChoices)history.replaceState(history.state,'',routes.publicPath(location.pathname,language)+location.search+location.hash);
  renderLanguage(route && !pendingChoices?route.language:language);
 },
 notify:notifyLanguageStatus
});
const languageControlIds=new WeakMap();
let nextLanguageControlId=0;
function closeLanguageMenu(node,restoreFocus=false){
 const menu=node.querySelector('.app-language-menu');
 if(menu?.matches(':popover-open'))menu.hidePopover();
 if(restoreFocus)node.querySelector('.app-language-control')?.focus();
}
function openLanguageMenu(event,node){
 node.querySelector('.app-language-control')?.setAttribute('aria-expanded',String(event.newState==='open'));
 if(event.newState!=='open')return;
 const menu=event.target,button=node.querySelector('.app-language-control'),rect=button.getBoundingClientRect();
 const width=Math.min(190,window.innerWidth-24);
 menu.style.width=`${width}px`;
 menu.style.left=`${Math.max(12,Math.min(rect.right-width,window.innerWidth-width-12))}px`;
 menu.style.top=`${rect.bottom+118<window.innerHeight?rect.bottom+8:Math.max(12,rect.top-118)}px`;
 requestAnimationFrame(()=>{
  if(menu.matches(':popover-open'))menu.querySelector('[aria-checked="true"]')?.focus();
 });
}
function languageMenuKeydown(event,node){
 const options=[...node.querySelectorAll('[role="menuitemradio"]')];
 const index=options.indexOf(document.activeElement);
 let next;
 if(event.key==='ArrowDown')next=(index+1)%options.length;
 if(event.key==='ArrowUp')next=(index+options.length-1)%options.length;
 if(event.key==='Home')next=0;
 if(event.key==='End')next=options.length-1;
 if(next!==undefined){event.preventDefault();options[next].focus();}
 if(event.key==='Tab')closeLanguageMenu(node,true);
 if(event.key==='Escape'){event.preventDefault();closeLanguageMenu(node,true);}
}
export function renderControls(){
 const label=T('common.chooseLanguage');
 for(const node of document.querySelectorAll('[data-app-language-control]')){
  if(!languageControlIds.has(node))languageControlIds.set(node,`app-language-menu-${++nextLanguageControlId}`);
  const id=languageControlIds.get(node);
  render(html`<button type="button" class="app-language-control" aria-label=${`${label}: ${engine.language==='en'?'English':'Español'}`} title=${label} aria-haspopup="menu" aria-expanded=${String(Boolean(node.querySelector('.app-language-menu:popover-open')))} aria-controls=${id} popovertarget=${id}
   @keydown=${event=>{if(event.key==='ArrowDown'||event.key==='ArrowUp'){event.preventDefault();node.querySelector('.app-language-menu').showPopover();}}}>
   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><circle cx="12" cy="12" r="9"></circle><path d="M3 12h18M12 3c5 5 5 13 0 18-5-5-5-13 0-18Z"></path></svg><span class="app-language-code" aria-hidden="true">${engine.language.toUpperCase()}</span>
   ${node.closest('.private-sidebar-header-actions')?html`<svg class="app-language-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6"></path></svg>`:null}
  </button><div id=${id} class="app-language-menu" popover="auto" role="menu" aria-label=${label}
   @beforetoggle=${event=>openLanguageMenu(event,node)} @keydown=${event=>languageMenuKeydown(event,node)}>
   ${[['es','Español'],['en','English']].map(([code,name])=>html`<button type="button" role="menuitemradio" tabindex="-1" aria-checked=${String(engine.language===code)} @click=${()=>{closeLanguageMenu(node,true);choose(code);}}><span lang=${code}>${name}</span><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="m5 12 4 4 10-10"/></svg></button>`)}
  </div><span class="app-language-feedback ${status.endsWith('Error')?'error':''}" role="status" aria-live="polite">${status?T('common.language'+({saving:'Saving',saved:'Saved',saveError:'SaveError',loadError:'LoadError'}[status])):''}</span>`,node);
 }
 const account=document.querySelector('#account-personal-language');if(account)account.value=engine.language;
 const message=document.querySelector('#app-language-status');if(message){message.textContent=status?T('common.language'+({saving:'Saving',saved:'Saved',saveError:'SaveError',loadError:'LoadError'}[status])):'';message.classList.toggle('error',status.endsWith('Error'));}
}
export function initializeLanguage(){
 const route=/^\/en(?:\/|$)/.test(window.location.pathname)?'en':'es';
 // Public URLs determine the page. A visit never writes a user preference.
 const isPublic=!/^\/(private|admin|chat)(\/|$)/.test(window.location.pathname);
 engine.changeLanguage(isPublic?route:store.language);document.documentElement.lang=engine.language;
 document.addEventListener('change',event=>{if(event.target.id==='account-personal-language'){event.stopImmediatePropagation();choose(event.target.value);}},true);
 document.addEventListener('security-auth-changed',event=>I18n.setUser(event.detail));
 window.addEventListener('popstate',syncRoute);
 window.addEventListener('resize',()=>document.querySelectorAll('[data-app-language-control]').forEach(node=>closeLanguageMenu(node)));
 document.addEventListener('scroll',event=>{if(!event.target.closest?.('.app-language-menu'))document.querySelectorAll('[data-app-language-control]').forEach(node=>closeLanguageMenu(node));},true);
 window.T=T;window.I18n=I18n;
}
